import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { translations } from '../../data/i18n';
import { siteConfig } from '../../data/site';
import { getServiceText, services } from '../../data/services';
import { availabilityNotes } from '../../data/bookingAvailability';
import {
  getBookingHours,
  getMinBookingDate,
  isLikelyBot,
  isTimeWithinBookingHours,
} from '../../utils/booking';
import { buildBookingWhatsAppUrl } from '../../utils/whatsapp';
import Reveal from '../ui/Reveal';

const Section = styled.section`
  background: var(--cream);
  padding: 0 28px 95px;
  margin-top: -72px;
  position: relative;
  z-index: 5;

  @media (max-width: 900px) {
    margin-top: 0;
    padding: 34px 18px 80px;
  }

  @media (max-width: 390px) {
    padding-left: 14px;
    padding-right: 14px;
  }
`;

const Card = styled.div`
  max-width: var(--container);
  width: 100%;
  margin: 0 auto;
  background: var(--cream-card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;

  display: grid;
  grid-template-columns: 310px minmax(0, 1fr);

  @media (max-width: 1050px) {
    grid-template-columns: 1fr;
  }
`;

const Intro = styled.div`
  background:
    radial-gradient(circle at top left, rgba(221,161,47,0.16), transparent 42%),
    var(--cream-soft);
  padding: 44px 42px;
  border-right: 1px solid rgba(0,0,0,0.08);

  small {
    display: block;
    color: var(--gold-dark);
    text-transform: uppercase;
    font-weight: 950;
    letter-spacing: 4px;
    margin-bottom: 14px;
  }

  h2 {
    margin: 0 0 20px;
    max-width: 250px;
    color: var(--black);
    font-size: clamp(2.15rem, 3vw, 3rem);
    line-height: 1.06;
    text-transform: uppercase;
    letter-spacing: -1.4px;
  }

  p {
    margin: 0 0 24px;
    color: var(--muted);
    line-height: 1.65;
  }

  @media (max-width: 1050px) {
    h2 {
      max-width: none;
    }
  }

  @media (max-width: 680px) {
    padding: 34px 28px;
  }
`;

const SpamNote = styled.div`
  color: #245a34;
  background: rgba(47,107,63,0.1);
  border: 1px solid rgba(47,107,63,0.16);
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.88rem;
  line-height: 1.45;
`;

const AvailabilityNote = styled.div`
  margin-top: 12px;
  color: #5f4f3e;
  background: rgba(255,255,255,0.72);
  border: 1px solid rgba(221,161,47,0.18);
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 750;
  line-height: 1.45;
`;

const Form = styled.form`
  padding: 44px 42px;
  display: grid;
  grid-template-columns: 1.05fr 1.05fr 1.05fr 1.25fr;
  gap: 25px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 30px 22px;
    gap: 34px;
  }

  @media (max-width: 390px) {
    padding: 26px 18px;
  }
`;

const Group = styled.div`
  min-width: 0;

  label {
    display: block;
    margin-bottom: 12px;
    color: var(--black);
    font-size: 0.78rem;
    font-weight: 950;
    text-transform: uppercase;
  }

  input,
  select,
  textarea {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    border: 1px solid #ded4c6;
    border-radius: 12px;
    background: #fff;
    color: var(--text);
    padding: 15px 16px;
    font-size: 1rem;
    outline: none;
    transition: border 0.2s ease, box-shadow 0.2s ease;
    -webkit-appearance: none;
    appearance: none;
  }

  input[type='date'],
  input[type='time'] {
    display: block;
    height: 58px;
    line-height: 1.2;
    text-align: left;
  }

  input[type='date']::-webkit-date-and-time-value,
  input[type='time']::-webkit-date-and-time-value {
    text-align: left;
  }

  input[type='date']::-webkit-calendar-picker-indicator,
  input[type='time']::-webkit-calendar-picker-indicator {
    opacity: 0.8;
    cursor: pointer;
  }

  input:disabled {
    color: var(--muted);
    background: #f2ede5;
    cursor: not-allowed;
  }

  input:focus,
  select:focus,
  textarea:focus {
    border-color: rgba(221,161,47,0.85);
    box-shadow: 0 0 0 4px rgba(221,161,47,0.16);
  }

  textarea {
    min-height: 92px;
    resize: vertical;
  }
`;

const Step = styled.div`
  width: 30px;
  height: 30px;
  margin-bottom: 16px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(221,161,47,0.18);
  color: var(--gold-dark);
  font-weight: 950;
`;

const Hint = styled.div`
  margin-top: 11px;
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const Fields = styled.div`
  display: grid;
  gap: 10px;
`;

const SubmitButton = styled.button`
  margin-top: 4px;
  min-height: 58px;
  border-radius: 13px;
  cursor: pointer;
  background: linear-gradient(135deg, var(--gold), var(--gold-light));
  color: #fff;
  font-weight: 950;
  text-transform: uppercase;
  box-shadow: 0 14px 30px rgba(221,161,47,0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 36px rgba(221,161,47,0.36);
  }
`;

const Error = styled.div`
  color: var(--red);
  font-weight: 850;
  font-size: 0.92rem;
`;

const HiddenField = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

function BookingSection({ language }) {
  const t = translations[language].booking;
  const minBookingDate = getMinBookingDate();

  const [formStartedAt] = useState(Date.now());
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');

  const [booking, setBooking] = useState({
    serviceId: services[0].id,
    date: minBookingDate,
    time: '',
    name: '',
    phone: '',
    email: '',
    note: '',
  });

  const selectedService = useMemo(
    () => services.find((service) => service.id === booking.serviceId) || services[0],
    [booking.serviceId]
  );

  const selectedServiceText = getServiceText(selectedService, language);
  const bookingHours = useMemo(() => getBookingHours(booking.date), [booking.date]);

  function updateField(field, value) {
    setBooking((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function updateDate(date) {
    const nextHours = getBookingHours(date);

    setBooking((prev) => ({
      ...prev,
      date,
      time: isTimeWithinBookingHours(prev.time, nextHours) ? prev.time : '',
    }));
    setError('');
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (isLikelyBot({ honeypot, formStartedAt })) {
      return;
    }

    if (
      !booking.name.trim() ||
      !booking.phone.trim() ||
      !booking.date ||
      !booking.time
    ) {
      setError(t.error);
      return;
    }

    if (!bookingHours) {
      setError(t.closedError);
      return;
    }

    if (!isTimeWithinBookingHours(booking.time, bookingHours)) {
      setError(t.timeError);
      return;
    }

    const url = buildBookingWhatsAppUrl({
      whatsappNumber: siteConfig.whatsappNumber,
      language,
      booking,
      serviceText: selectedServiceText,
    });

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <Section id="booking">
      <Reveal>
        <Card>
          <Intro>
            <small>{t.eyebrow}</small>
            <h2>{t.title}</h2>
            <p>{t.text}</p>
            <SpamNote>🛡️ {t.spamInfo}</SpamNote>
            <AvailabilityNote>{availabilityNotes[language]}</AvailabilityNote>
          </Intro>

          <Form onSubmit={handleSubmit}>
            <HiddenField
              type="text"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />

            <Group>
              <Step>1</Step>
              <label htmlFor="booking-service">{t.step1}</label>

              <select
                id="booking-service"
                value={booking.serviceId}
                onChange={(event) => updateField('serviceId', event.target.value)}
              >
                {services.map((service) => {
                  const serviceText = getServiceText(service, language);

                  return (
                    <option key={service.id} value={service.id}>
                      {serviceText.title}
                    </option>
                  );
                })}
              </select>

              <Hint>
                {t.priceFrom} {selectedServiceText.priceFrom} · {selectedServiceText.duration}
              </Hint>
            </Group>

            <Group>
              <Step>2</Step>
              <label htmlFor="booking-date">{t.step2}</label>

              <input
                id="booking-date"
                type="date"
                min={minBookingDate}
                value={booking.date}
                onChange={(event) => updateDate(event.target.value)}
                required
              />

              <Hint>⏱ {t.botHint}</Hint>
            </Group>

            <Group>
              <Step>3</Step>
              <label htmlFor="booking-time">{t.step3}</label>

              <input
                id="booking-time"
                type="time"
                min={bookingHours?.min}
                max={bookingHours?.max}
                step="900"
                value={booking.time}
                onChange={(event) => updateField('time', event.target.value)}
                aria-describedby="booking-time-hint"
                disabled={!bookingHours}
                required
              />

              <Hint id="booking-time-hint">
                {bookingHours
                  ? `🕒 ${t.timeHint} ${bookingHours.min}–${bookingHours.max} ${t.clock}`
                  : `ℹ️ ${t.closedHint}`}
              </Hint>
            </Group>

            <Group>
              <Step>4</Step>
              <label>{t.step4}</label>

              <Fields>
                <input
                  id="booking-name"
                  value={booking.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  placeholder={t.name}
                  aria-label={t.name}
                  autoComplete="name"
                  required
                />

                <input
                  id="booking-phone"
                  type="tel"
                  value={booking.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder={t.phone}
                  aria-label={t.phone}
                  autoComplete="tel"
                  inputMode="tel"
                  required
                />

                <input
                  id="booking-email"
                  type="email"
                  value={booking.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  placeholder={t.email}
                  aria-label={t.email}
                  autoComplete="email"
                />

                <textarea
                  id="booking-note"
                  value={booking.note}
                  onChange={(event) => updateField('note', event.target.value)}
                  placeholder={t.note}
                  aria-label={t.note}
                  maxLength="500"
                />

                <SubmitButton type="submit">{t.submit}</SubmitButton>

                {error && <Error role="alert">{error}</Error>}
              </Fields>
            </Group>
          </Form>
        </Card>
      </Reveal>
    </Section>
  );
}

export default BookingSection;
